import { buildCanonicalProductPathFromRecord } from "@/lib/product-url";

export type ShareOutcome = "shared" | "copied" | "cancelled" | "manual";

function shouldUseNativeShare(): boolean {
  // Desktop Safari supports `navigator.share()` and shows a popover anchored to the button.
  // For our UX we only want the native share sheet on small/mobile viewports.
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 639px)").matches;
}

export function getProductPageUrl(
  productId: string,
  opts?: { product?: Record<string, unknown> | null }
): string | null {
  const path = opts?.product ? buildCanonicalProductPathFromRecord(opts.product, productId) : null;
  if (!path) return null;
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

export async function shareUrl({
  url,
  title,
}: {
  url: string;
  title?: string;
}): Promise<ShareOutcome> {
  // Prefer native share sheet when available (mostly mobile).
  if (
    shouldUseNativeShare() &&
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function"
  ) {
    try {
      await navigator.share({ title, url });
      return "shared";
    } catch (err) {
      // User cancelled / dismissed the share sheet.
      if (err instanceof DOMException && err.name === "AbortError") return "cancelled";
      // Fall through to copy fallback.
    }
  }

  // Fallback: copy link to clipboard.
  try {
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch (err) {
    console.warn("Clipboard API unavailable:", err);
    // Last-resort fallback: let the user manually copy.
    if (typeof window !== "undefined") {
      window.prompt("Copy link", url);
      return "manual";
    }
    throw new Error("Failed to share url");
  }
}

export async function shareProductPageLink({
  productId,
  title,
  product,
}: {
  productId: string;
  title?: string;
  product?: Record<string, unknown> | null;
}): Promise<ShareOutcome> {
  const url = getProductPageUrl(productId, { product });
  if (!url) {
    throw new Error(`Unable to build canonical product URL for ${productId}`);
  }
  return await shareUrl({ url, title });
}
